import { useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { debounce, orderBy } from 'es-toolkit';
import { format } from 'date-fns';
import { tz } from '@date-fns/tz';
import { FormControl, InputAdornment, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { koKR } from '@mui/x-data-grid/locales';
import { Search } from '@mui/icons-material';

import { timeZoneAtom } from '@/interfaces/utils';
import { auditLogAtom, auditLogPeriodAtom } from '@/interfaces/audit';
import { compareInLowerCase } from '@/components/common/utils';
import { SettingLayout } from '@/components/setting/SettingLayout';
import DatePicker from '@/components/common/DatePicker';

import type { ChangeEvent } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type { AuditLog } from '@/@types/audit';

export default function OperationLog() {
  const { data: logs } = useAtomValue(auditLogAtom);
  const timeZone = useAtomValue(timeZoneAtom);

  const [rows, setRows] = useState<AuditLog[]>([]);
  const [logType, setLogType] = useState('all');
  const [query, setQuery] = useState('');
  const { t, i18n } = useTranslation();

  const isKorean = i18n.language.startsWith('ko');
  const locale = isKorean ? koKR.components.MuiDataGrid.defaultProps.localeText : null;
  const timeFormat = isKorean ? 'yyyy.MM.dd a hh:mm' : 'MM/dd/yyyy hh:mm a';

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'timestamp',
        headerName: t('Devices.label.time'),
        flex: 1,
        sortable: true,
        type: 'dateTime',
        valueFormatter: (value: string) => format(value, timeFormat, { in: tz(timeZone) }),
      },
      {
        field: 'type',
        headerName: t('Log.label.logType'),
        flex: 1,
        sortable: false,
        valueFormatter: (value: string) => t(`Log.label.${value.toLowerCase()}`),
      },
      { field: 'message', headerName: t('Common.label.message'), flex: 4, sortable: false },
      { field: 'email', headerName: t('Common.label.user'), flex: 1, sortable: false },
    ],
    [t, timeFormat, timeZone],
  );

  useEffect(() => {
    if (logs === undefined) return;
    setLogType('all');
    setRows(orderBy(logs, ['timestamp'], ['desc']));
  }, [logs]);

  const filterLogs = useMemo(
    () =>
      debounce((queryFilter: string, logTypeFilter: string) => {
        const filterResult = logs.filter(
          (row) =>
            compareInLowerCase(row.message, queryFilter) &&
            (logTypeFilter === 'all' ? true : row.type === logTypeFilter),
        );
        setRows(filterResult);
      }, 500),
    [logs],
  );
  function onQueryChange(e: ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;
    setQuery(value);
    filterLogs(value, logType);
  }
  function onSelectChange(e: SelectChangeEvent) {
    const { value } = e.target;
    setLogType(value);
    filterLogs(query, value);
  }

  return (
    <SettingLayout>
      <Stack gap={3} sx={{ height: '100%' }}>
        <Typography variant="h5" fontWeight="bold">
          {t('Log.label.operationLog')}
        </Typography>

        <Stack direction="row" gap={2}>
          <TextField
            value={query}
            placeholder={t('Common.label.search')}
            onChange={onQueryChange}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: '300px', bgcolor: 'white' }}
          />
          <FormControl>
            <InputLabel>{t('Log.label.logType')}</InputLabel>
            <Select
              label={t('Log.label.logType')}
              value={logType}
              onChange={onSelectChange}
              sx={{ width: 160, bgcolor: 'white' }}
            >
              <MenuItem value="all">{t('Common.label.all')}</MenuItem>
              <MenuItem value="SYSTEM">{t('Log.label.system')}</MenuItem>
              <MenuItem value="DEVICE">{t('Common.label.device')}</MenuItem>
              <MenuItem value="FDA">FDA</MenuItem>
            </Select>
          </FormControl>
          <DatePicker periodAtom={auditLogPeriodAtom} />
        </Stack>

        <DataGrid
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          localeText={locale}
          columns={columns}
          rows={rows}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Stack>
    </SettingLayout>
  );
}
